import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState, useCallback } from 'react'
import { notification } from 'antd'
import { formatStartDate, formatEndDate } from '@/utils/format'
import type { PaginationState } from '@tanstack/react-table'
import { getCoreRowModel, getPaginationRowModel } from '@tanstack/react-table'
import { DataTable } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination'
import { ConfirmModal } from '@/components/ui/confirm-modal'
import { PageHeader } from '@/components/ui/page-header'
import {
  fetchVersions,
  updateVersion,
  deleteVersion,
  getVersionById,
} from '@/api/versions.api'
import { buildColumns } from './columns'
import type { VersionItem } from '@/types/api'
import { VersionFilterPanel } from './VersionFilterPanel'
import type { VersionFilterValues } from './VersionFilterPanel'
import { VersionFormDrawer } from './VersionFormDrawer'
import { VersionDetailDrawer } from './VersionDrawer'
import { downloadFileWithName } from '@/utils/download'
import { usePermission } from '@/hooks/use-permission'
import { exportVersion, useTableExport } from '@/api/dowload-excel.api'
import type { ExportPayload } from '@/api/dowload-excel.api'

const DEFAULT_FILTER: VersionFilterValues = {
  versionName: '',
  time: undefined,
  status: undefined,
}

export function VersionsPage() {
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })
  const [showFilter, setShowFilter] = useState(false)
  const [filter, setFilter] = useState<VersionFilterValues>(DEFAULT_FILTER)
  const [activeFilters, setActiveFilters] =
    useState<VersionFilterValues>(DEFAULT_FILTER)
  const [isExporting, setIsExporting] = useState(false)
  const canView = usePermission('version-view')
  const canUpdate = usePermission('version-update')
  const canDelete = usePermission('version-delete')
  const canExport = usePermission('version-export')

  // Modal & Form state
  const [viewingItem, setViewingItem] = useState<VersionItem | null>(null)
  const [isFormDrawerOpen, setIsFormDrawerOpen] = useState(false)
  const [formMode, setFormMode] = useState<'create' | 'update'>('create')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [deletingItem, setDeletingItem] = useState<VersionItem | null>(null)
  const [togglingItem, setTogglingItem] = useState<VersionItem | null>(null)

  const queryClient = useQueryClient()

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['versions', pageIndex, pageSize, activeFilters],
    queryFn: ({ signal }) => {
      return fetchVersions(
        pageIndex,
        pageSize,
        {
          versionName: activeFilters.versionName || undefined,
          status: activeFilters.status,
          fromDate: formatStartDate(activeFilters.time?.from) || undefined,
          toDate:
            formatEndDate(activeFilters.time?.to || activeFilters.time?.from) ||
            undefined,
        },
        signal,
      )
    },
    refetchOnMount: 'always',
  })

  const { mutate: toggleStatus, isPending: isToggling } = useMutation({
    mutationFn: async (item: VersionItem) => {
      const detail = await getVersionById(item.id)

      return updateVersion(item.id, {
        name: detail.name,
        isChangeApkFile: false,
        isChangeGuildFile: false,
        description: detail.description || '',
        status: item.status === 1 ? 0 : 1,
      })
    },
    onSuccess: (response: any) => {
      const successMessage =
        response.responseMessage || 'Cập nhật trạng thái thành công!'
      notification.success({ title: successMessage })
      queryClient.invalidateQueries({ queryKey: ['versions'] })
    },
    onSettled: () => {
      setTogglingItem(null)
    },
  })

  const { mutate: removeItem, isPending: isRemoving } = useMutation({
    mutationFn: async (item: VersionItem) => deleteVersion(item.id),
    onSuccess: (response: any) => {
      const successMessage =
        response.responseMessage || 'Xóa phiên bản thành công!'
      notification.success({ title: successMessage })
      queryClient.invalidateQueries({ queryKey: ['versions'] })
    },
    onSettled: () => {
      setDeletingItem(null)
    },
  })

  const handleDownload = async (url: string, fileName?: string) => {
    try {
      await downloadFileWithName({ url, fileName })
    } catch {
      window.open(url, '_blank')
    }
  }

  const columns = useMemo(
    () =>
      buildColumns({
        pageIndex,
        pageSize,
        canView,
        canUpdate,
        canDelete,
        onViewDetail: (item) => setViewingItem(item),
        onUpdate: (item) => {
          setFormMode('update')
          setEditingId(item.id)
          setIsFormDrawerOpen(true)
        },
        onDelete: (item) => setDeletingItem(item),
        onToggleStatus: (item) => setTogglingItem(item),
        onDownload: handleDownload,
      }),
    [pageIndex, pageSize, canView, canUpdate, canDelete],
  )

  const rows = useMemo(() => data?.items ?? [], [data?.items])

  // Hàm gọi API xuất Excel cho Quản lý Phiên bản
  const handleExportApi = useCallback(
    async (payload: ExportPayload) => {
      try {
        setIsExporting(true)
        await exportVersion(
          {
            page: pageIndex,
            size: pageSize,
            versionName: activeFilters.versionName || undefined,
            status: activeFilters.status,
            fromDate: formatStartDate(activeFilters.time?.from) || undefined,
            toDate:
              formatEndDate(activeFilters.time?.to || activeFilters.time?.from) ||
              undefined,
          },
          payload,
          'Danh_sach_phien_ban',
        )
      } finally {
        setIsExporting(false)
      }
    },
    [pageIndex, pageSize, activeFilters],
  )

  const { handleExport } = useTableExport(
    'Danh sách phiên bản',
    columns,
    handleExportApi,
  )

  if (isLoading) {
    return (
      <section className="space-y-4">
        <PageHeader title="DANH SÁCH PHIÊN BẢN" isLoading />
        <div className="mt-6 text-center">Đang tải...</div>
      </section>
    )
  }

  return (
    <section className="space-y-4">
      <PageHeader
        title="DANH SÁCH PHIÊN BẢN"
        onToggleFilter={() => setShowFilter((v) => !v)}
        onExport={canExport ? handleExport : undefined}
        disabled={!(rows.length > 0)}
        isExporting={isExporting}
      />

      {showFilter && (
        <VersionFilterPanel
          values={filter}
          onChange={setFilter}
          onSearch={() => {
            const trimmedFilter = {
              ...filter,
              versionName: filter.versionName.trim(),
            }
            setFilter(trimmedFilter)
            setActiveFilters(trimmedFilter)
            setPagination((s) => ({ ...s, pageIndex: 0 }))
            refetch()
          }}
        />
      )}

      <DataTable
        columns={columns}
        data={rows}
        tableOptions={{
          state: { pagination: { pageIndex, pageSize } },
          onPaginationChange: setPagination,
          getPaginationRowModel: getPaginationRowModel(),
          getCoreRowModel: getCoreRowModel(),
          manualPagination: true,
          pageCount: data?.totalPages,
        }}
        footer={
          <Pagination
            page={pageIndex + 1}
            pageCount={data?.totalPages ?? 1}
            pageSize={pageSize}
            totalCount={data?.total ?? 0}
            onPageChange={(p) =>
              setPagination((s) => ({ ...s, pageIndex: p - 1 }))
            }
            onPageSizeChange={(size) =>
              setPagination({ pageIndex: 0, pageSize: size })
            }
          />
        }
      />

      <VersionDetailDrawer
        versionId={viewingItem?.id || null}
        onClose={() => setViewingItem(null)}
        onDownload={handleDownload}
        onUpdate={(item) => {
          setViewingItem(null)
          setFormMode('update')
          setEditingId(item.id)
          setIsFormDrawerOpen(true)
        }}
      />

      {isFormDrawerOpen && (
        <VersionFormDrawer
          isOpen={isFormDrawerOpen}
          mode={formMode}
          versionId={editingId}
          onClose={() => {
            setIsFormDrawerOpen(false)
            setEditingId(null)
          }}
        />
      )}

      <ConfirmModal
        isOpen={!!togglingItem}
        title="Xác nhận"
        description={
          <>
            Bạn chắc chắn muốn{' '}
            {togglingItem?.status === 1 ? 'vô hiệu hóa' : 'kích hoạt'} phiên bản{' '}
            <strong>{togglingItem?.name}</strong> này?
          </>
        }
        variant="info"
        isLoading={isToggling}
        onClose={() => setTogglingItem(null)}
        onConfirm={() => togglingItem && toggleStatus(togglingItem)}
      />

      <ConfirmModal
        isOpen={!!deletingItem}
        title="Xóa phiên bản"
        description={
          <>
            Bạn có chắc chắn muốn xóa phiên bản{' '}
            <strong>{deletingItem?.name}</strong> này?
          </>
        }
        variant="danger"
        isLoading={isRemoving}
        onClose={() => setDeletingItem(null)}
        onConfirm={() => deletingItem && removeItem(deletingItem)}
      />
    </section>
  )
}
